import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["results"]
  
  connect() {
    this.timeout = null
    this.initializeDropdownHandlers()
    this.updateFilterByState()
  }
  
  handleAddressKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.saveAddress(event)
    }
  }
  
  saveAddress(event) {
    event.preventDefault()
    const newAddress = document.getElementById('address-input').value.trim()
    
    if (!newAddress) {
      alert('Please enter a valid address')
      return
    }
    
    // Update the hidden field in the form
    const form = this.element.querySelector('form')
    const searchAddressField = form.querySelector('input[name="search_address"]')
    if (searchAddressField) {
      searchAddressField.value = newAddress
    }
    
    // Clear the location selection when using custom address
    const searchLocationField = form.querySelector('input[name="search_location_id"]')
    if (searchLocationField) {
      searchLocationField.value = ''
    }
    
    // Update location filter display to show no selection
    this.updateLocationFilterDisplay()
    
    // Trigger search with new address
    this.performSearch()
  }
  
  clearCustomAddress(event) {
    event.preventDefault()
    
    // Clear the input field
    const addressInput = document.getElementById('address-input')
    if (addressInput) {
      addressInput.value = ''
    }
    
    // Clear the hidden field in the form
    const form = this.element.querySelector('form')
    const searchAddressField = form.querySelector('input[name="search_address"]')
    if (searchAddressField) {
      searchAddressField.value = ''
    }
    
    // Trigger search to return to default location
    this.performSearch()
  }
  
  search(event) {
    event.preventDefault()
    
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    
    const delay = event.target.type === "text" ? 500 : 0
    
    this.timeout = setTimeout(() => {
      this.performSearch()
    }, delay)
  }
  
  sendReferral(event) {
    event.preventDefault()
    
    const organizationId = event.currentTarget.dataset.organizationId
    
    if (!organizationId) {
      console.error('Organization ID not found')
      return
    }
    
    window.location.href = `/referrals/new?organization_id=${organizationId}`
  }
  
  showOrganizationModal(event) {
    const button = event.currentTarget
    const locationId = button.dataset.locationId
    
    if (!locationId) {
      console.error('Location ID not found')
      return
    }
    
    fetch(`/referral_partners/organization_modal?location_id=${locationId}`, {
      method: 'GET',
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.text())
    .then(html => {
      const modalContent = document.getElementById('organization-modal-content')
      modalContent.innerHTML = html
      
      // Add event listener to the new send referral button
      const sendReferralButton = modalContent.querySelector('[data-action*="sendReferral"]')
      if (sendReferralButton) {
        sendReferralButton.addEventListener('click', (e) => {
          this.sendReferral(e)
        })
      }
    })
    .catch(error => {
      console.error('Error loading modal content:', error)
    })
  }

  toggleDropdown(event) {
    document.querySelectorAll(".custom-dropdown").forEach((dropdown) => {
      dropdown.classList.add("d-none")
    })

    const dropdown = event.target
      .closest(".filter-item")
      .querySelector(".custom-dropdown")

    this.toggleSelectAllButton(dropdown)
    dropdown.classList.toggle("d-none")
  }

  clickDropdownFilterItem(event) {
    const dropdownFilterItem = event.target.closest(".dropdown-filter-item")
    const dropdown = event.target.closest(".custom-dropdown")
    const el = dropdown.querySelector('input.filter-input[type="hidden"]')
    const selectedValue = dropdownFilterItem.dataset.dropdownFilterItemValue
    
    // Handle different filter types
    if (el.name === 'radius') {
      // Single-select logic for distance
      dropdown.querySelectorAll('.dropdown-filter-item').forEach(item => {
        item.classList.remove('active-item')
      })
      dropdownFilterItem.classList.add('active-item')
      el.value = selectedValue
      el.disabled = false
    } else if (el.name === 'search_location_id') {
      // Single-select logic for location
      dropdown.querySelectorAll('.dropdown-filter-item').forEach(item => {
        item.classList.remove('active-item')
      })
      dropdownFilterItem.classList.add('active-item')
      el.value = selectedValue
      el.disabled = false
      
      // Update the location display text
      const locationDisplay = dropdownFilterItem.dataset.locationDisplay
      if (locationDisplay) {
        const locationTextSpan = dropdown.closest('.filter-item').querySelector('.location-text')
        if (locationTextSpan) {
          locationTextSpan.textContent = locationDisplay
        }
      }
      
      // Clear custom address when selecting location
      const form = this.element.querySelector('form')
      const searchAddressField = form.querySelector('input[name="search_address"]')
      if (searchAddressField) {
        searchAddressField.value = ''
      }
      
      // Clear the visible address input
      const addressInput = document.getElementById('address-input')
      if (addressInput) {
        addressInput.value = ''
      }
    } else {
      // Multi-select logic for specialty
      dropdownFilterItem.classList.toggle("active-item")
      const originValue = el.value
      el.disabled = false

      if (!dropdownFilterItem.classList.contains("active-item")) {
        el.value = originValue
          .split(", ")
          .filter((value) => value !== selectedValue)
          .join(", ")
      } else if (!originValue.split(",").some((value) => value === selectedValue)) {
        el.value = originValue ? `${originValue}, ${selectedValue}` : selectedValue
      }
    }

    this.toggleSelectAllButton(dropdown)
    this.updateFilterByState()
    
    this.updateFilterButtonText(dropdown, el)
    
    setTimeout(() => {
      this.performSearch()
    }, 100)
  }

  selectOrDeselectAll(event) {
    const dropdown = event.target.closest(".custom-dropdown")
    const hiddenInput = dropdown.querySelector('input.filter-input[type="hidden"]')
    const items = dropdown.querySelectorAll(".dropdown-filter-item")
    const activeItems = dropdown.querySelectorAll(".dropdown-filter-item.active-item")

    if (activeItems.length > 0) {
      hiddenInput.value = ""
      hiddenInput.disabled = true
      items.forEach((item) => item.classList.remove("active-item"))
    } else {
      const allIds = Array.from(items).map(item => item.dataset.dropdownFilterItemValue)
      hiddenInput.value = allIds.join(", ")
      hiddenInput.disabled = false
      items.forEach((item) => item.classList.add("active-item"))
    }

    this.toggleSelectAllButton(dropdown)
    this.updateFilterByState()
    this.updateFilterButtonText(dropdown, hiddenInput)
    
    setTimeout(() => {
      this.performSearch()
    }, 100)
  }

  toggleSelectAllButton(dropdown) {
    const selectAllButton = dropdown.querySelector('.select-all')
    if (!selectAllButton) return // Skip for single-select dropdowns
    
    const activeItems = dropdown.querySelectorAll(".dropdown-filter-item.active-item")

    if (activeItems.length > 0) {
      selectAllButton.textContent = "Deselect All"
    } else {
      selectAllButton.textContent = "Select All"
    }
  }

  updateFilterButtonText(dropdown, hiddenInput) {
    const filterBy = dropdown.closest('.filter-item').querySelector('.filter-by span')
    const selectedValue = hiddenInput.value
    
    if (hiddenInput.name === 'specialty') {
      if (selectedValue) {
        const selectedItems = selectedValue.split(', ')
        if (selectedItems.length === 1) {
          filterBy.innerHTML = `<i class="fa-solid fa-sliders me-2"></i>${selectedItems[0]}`
        } else {
          filterBy.innerHTML = `<i class="fa-solid fa-sliders me-2"></i>${selectedItems.length} Specialties`
        }
      } else {
        filterBy.innerHTML = `<i class="fa-solid fa-sliders me-2"></i>Specialty`
      }
    } else if (hiddenInput.name === 'radius') {
      filterBy.innerHTML = `<span class="text-muted">Within</span> ${selectedValue} miles`
    }
    // Location filter text is updated separately in clickDropdownFilterItem
  }

  updateLocationFilterDisplay() {
    const form = this.element.querySelector('form')
    const searchLocationField = form.querySelector('input[name="search_location_id"]')
    
    if (searchLocationField && !searchLocationField.value) {
      // Reset location filter to show default
      const locationFilterBy = document.querySelector('.filter-item .filter-by .location-text')
      if (locationFilterBy) {
        // Get the default location name from the original filter setup
        const defaultLocationName = locationFilterBy.getAttribute('data-default-name') || 'Location'
        locationFilterBy.textContent = defaultLocationName
      }
      
      // Remove active state from all location items
      const locationDropdown = searchLocationField.closest('.filter-item')?.querySelector('.custom-dropdown')
      if (locationDropdown) {
        locationDropdown.querySelectorAll('.dropdown-filter-item').forEach(item => {
          item.classList.remove('active-item')
        })
      }
      
      // Remove active state from location filter button
      const locationFilterButton = searchLocationField.closest('.filter-item')?.querySelector('.filter-by')
      if (locationFilterButton) {
        locationFilterButton.classList.remove('active')
      }
    }
  }

  initializeDropdownHandlers() {
    // Close dropdowns when clicking outside
    document.addEventListener("click", (event) => {
      if (!event.target.closest(".filter-item")) {
        this.handleFocusOutFilter()
      }
    })
  }

  handleFocusOutFilter() {
    setTimeout(() => {
      const openDropdown = document.querySelector(".custom-dropdown:not(.d-none)")
      if (openDropdown) {
        openDropdown.classList.add("d-none")
      }
    }, 100)
  }

  updateFilterByState() {
    document.querySelectorAll(".filter-item").forEach((filterItem) => {
      const dropdown = filterItem.querySelector(".custom-dropdown")
      if (!dropdown) return

      const hiddenInput = dropdown.querySelector('input.filter-input[type="hidden"]')
      const filterByElement = filterItem.querySelector(".filter-by")

      let isActive = false
      if (hiddenInput.name === 'specialty') {
        isActive = hiddenInput.value !== ''
      } else if (hiddenInput.name === 'radius') {
        isActive = hiddenInput.value !== '25'
      } else if (hiddenInput.name === 'search_location_id') {
        isActive = hiddenInput.value !== ''
      } else {
        const activeItems = dropdown.querySelectorAll(".dropdown-filter-item.active-item")
        isActive = activeItems.length > 0
      }

      if (isActive) {
        filterByElement.classList.add("active")
      } else {
        filterByElement.classList.remove("active")
      }
    })
  }
  
  performSearch() {
    const form = this.element.querySelector('form')
    const formData = new FormData(form)
    const params = new URLSearchParams(formData)
    
    // Reset to page 1 when performing a new search (filters change)
    params.set('page', '1')
    params.set('partial', 'true')
    
    fetch(`${form.action}?${params}`, {
      method: 'GET',
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.text())
    .then(html => {
      if (this.hasResultsTarget) {
        this.resultsTarget.innerHTML = html
      }
    })
    .catch(error => {
      console.error('Search error:', error)
    })
  }
  
  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }
}